import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission } from "imports/Utils";

import { GetCostTypesDocument, GetCostTypesQuery } from "__generated__/view/costtypes/graphql/queries.generated";
import { DeleteCostTypeDocument, DeleteCostTypeMutation } from "__generated__/view/costtypes/graphql/mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type CostTypeFromQuery = ChildPropsFromArray<GetCostTypesQuery, "costTypes">;

const CostTypes = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const { data, refetch, loading } = useQuery<GetCostTypesQuery>(GetCostTypesDocument);

	const [ deleteCostType ] = useMutation<DeleteCostTypeMutation>(DeleteCostTypeDocument);

	const { isAdmin, isMasterdata, isReadOnly } = usePermission();

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.CostTypes,
		content: data?.costTypes ?? [],
	});

	const onDeleteCostType = (row: CostTypeFromQuery) => {
		deleteCostType({
			variables: {
				id: row.id,
			},
		}).then(() => {
			refetch();
			refetchAfterDelete();
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("COST_TYPES")}
					<Helmet title={intl.formatMessage({ id: "COST_TYPES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/costtypes/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					columns={[ {
						id: "title_en",
						title: intl.formatMessage({ id: "LANGUAGE_EN" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "title_nl",
						title: intl.formatMessage({ id: "LANGUAGE_NL" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "title_de",
						title: intl.formatMessage({ id: "LANGUAGE_DE" }),
						sortable: true,
						filterComponent: "TEXT",
					} ]}
					rowActions={(isAdmin || isMasterdata) && !isReadOnly ? [ {
						icon: <EditIcon />,
						onClick: (row) => navigate(`/view/costtypes/${row.id}/edit`, { state: { costType: row } }),
					}, {
						icon: <DeleteIcon />,
						onClick: (row) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteCostType(row);
							});
						},
					} ] :  undefined} />
			</Card>
		</div>
	);
};

export default CostTypes;
